'use client';

import SyncIcon from '@mui/icons-material/Sync';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
	DataGridPro,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from '../../hooks/useDebounce';
import { SYNC_RESERVATION_IN_ACCESS_SYSTEM } from '../../mutations';
import { LIST_RESERVATION_ACCESS_SYSTEM_HISTORY } from '../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../types';
import { convertMuiFilterForV2Api, datagridFilterPanelAttributes, dayjs } from '../../utils';
import { DatagridLinearProgress } from '../loading';
import { UserModal } from '../modals/userModal';
import { RASHDetailModal } from './rashDetailModal';

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
};

const reservationIntegrationDisplayByValue = {
	AMANO: 'Amano',
	FLASH: 'Flash',
	GET_MY_PARKING: 'Get My Parking',
	PARKONECT: 'Parkonect',
	TIBA: 'Tiba',
	IP: 'IP',
	NONE: 'None',
} as const;

const reservationIntegrationValueByDisplay = {
	Amano: 'AMANO',
	Flash: 'FLASH',
	'Get My Parking': 'GET_MY_PARKING',
	Parkonect: 'PARKONECT',
	Tiba: 'TIBA',
	IP: 'IP',
	None: 'NONE',
} as const;

type ReservationAccessSystemHistoryTableProps = {
	tabNav?: JSX.Element;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	csvExportButton: ((props: any) => ReactElement) | null;
};

const dateOperators = getGridDateOperators();
const numericOperators = getGridNumericOperators();

export const ReservationAccessSystemHistoryTable = ({
	setShowDialog,
	setShowSnackbar,
	tabNav,
}: ReservationAccessSystemHistoryTableProps) => {
	const defaultFilters: any = useMemo(
		() => [
			{
				propertyName: 'status',
				op: 'in',
				value: ['Failed', 'Pending', 'New'],
			},
		],
		[],
	);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState(0);
	const [sortOrder, setSortOrder] = useState({ field: 'createdAt', sort: 'desc' });
	const [filters, setFilters] = useState(defaultFilters);
	const debouncedSetFilters = useDebounce(setFilters, 500);
	const [syncReservation] = useMutation(SYNC_RESERVATION_IN_ACCESS_SYSTEM);
	const [syncingRowId, setSyncingRowId] = useState<number | string>('');

	const {
		loading,
		data: reservationAccessSystemHistoryData,
		refetch,
	} = useQuery(LIST_RESERVATION_ACCESS_SYSTEM_HISTORY, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			filters,
		},
		context: { apiVersion: 'v2' },
		fetchPolicy: 'network-only',
	});

	const rashData = useMemo(() => {
		const rows =
			reservationAccessSystemHistoryData?.listReservationAccessSystemHistory?.reservationAccessSystemHistory;

		if (!rows?.length) {
			return [];
		}

		return rows.map((row) => ({
			...row,
			integration: reservationIntegrationDisplayByValue[row.integration] || row.integration,
			email: row.createdByUser?.email,
		}));
	}, [reservationAccessSystemHistoryData]);

	useEffect(() => {
		if (!loading && reservationAccessSystemHistoryData) {
			setRowCount(reservationAccessSystemHistoryData?.listReservationAccessSystemHistory?.total);
		}
	}, [loading, reservationAccessSystemHistoryData]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const handleSync = async (sessionId: number, facilityId: string, rashId: number | string) => {
		setSyncingRowId(rashId);
		try {
			await syncReservation({
				variables: {
					sessionId,
					facilityId,
				},
				context: { apiVersion: 'v2' },
			});
			setShowSnackbar({
				message: 'Successfully Synced Reservation In Access System',
				severity: 'success',
				autoHideDuration: 6000,
			});
			await refetch();
		} catch (err) {
			setShowSnackbar({
				message: err instanceof Error ? err.message : 'Error Syncing Reservation In Access System',
				severity: 'error',
				autoHideDuration: 6000,
			});
		} finally {
			setSyncingRowId('');
		}
	};

	const columns = [
		{
			field: 'actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				if (syncingRowId === params.row.id) {
					return <CircularProgress />;
				}
				return (
					<Tooltip title='Sync reservation in access system'>
						<IconButton
							disabled={!!syncingRowId && syncingRowId !== params.row.id}
							onClick={() => handleSync(params.row.sessionId, params.row.facilityId, params.row.id)}
						>
							<SyncIcon />
						</IconButton>
					</Tooltip>
				);
			},
		},
		{
			field: 'createdAt',
			headerName: 'Created At',
			filterOperators: dateOperators,
			valueGetter: (params) => {
				if (!params.value) return null;
				return dayjs
					.utc(params.value)
					.tz(params.row.facilityTimezone || 'America/Chicago')
					.toDate();
			},
			renderCell: (params) => {
				if (!params.value) return null;
				return dayjs(params.value)
					.tz(params.row.facilityTimezone || 'America/Chicago')
					.format('MM/DD/YYYY h:mm a z');
			},
			flex: 1.5,
		},
		{
			field: 'email',
			headerName: 'Created By',
			renderCell: ({ row }) =>
				row.createdByUser?.internalUseOnly ? (
					`${row.createdByUser.firstName} ${row.createdByUser.lastName}`
				) : (
					<a
						style={style}
						onClick={() =>
							setShowDialog(
								<UserModal
									dismissText='Close'
									user={row.createdByUser}
									handleDismiss={() => setShowDialog(null)}
								/>,
							)
						}
					>
						{row.email}
					</a>
				),
			flex: 1.5,
		},
		{
			field: 'sessionId',
			headerName: 'Session Id',
			filterOperators: numericOperators,
			flex: 1,
		},
		{
			field: 'sessionHash',
			headerName: 'Session Hash',
			flex: 1.2,
		},
		{
			field: 'integration',
			headerName: 'Integration',
			flex: 1,
			type: 'singleSelect',
			valueOptions: ['Amano', 'Flash', 'Parkonect', 'Tiba', 'Get My Parking'],
		},
		{
			field: 'action',
			headerName: 'Action',
			type: 'singleSelect',
			valueOptions: ['CREATE', 'UPDATE', 'CANCEL'],
			flex: 1,
		},
		{
			field: 'status',
			headerName: 'Status',
			type: 'singleSelect',
			valueOptions: ['New', 'Pending', 'Failed', 'Success'],
			flex: 1,
			renderCell: (params) => {
				if (params.row.status === 'Failed') {
					return (
						<a
							style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
							onClick={() =>
								setShowDialog(
									<RASHDetailModal
										rashId={Number(params.row.id)}
										handleClose={() => {
											setShowDialog(null);
										}}
									/>,
								)
							}
						>
							{params.row.status}
						</a>
					);
				}

				return params.row.status;
			},
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			flex: 1,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility Number',
			flex: 1,
		},
	];

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		if (!sortModel.length) {
			return setSortOrder({ field: 'id', sort: 'asc' });
		}

		const { field, sort } = sortModel[0];
		return setSortOrder({ field, sort });
	}, []);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			const { items: filterItems } = filterModel;

			if (!filterItems.length) {
				debouncedSetFilters([]);
				return;
			}

			const queryFilters = filterItems
				.map((filter) => {
					const { field, operator, value: rawValue } = filter;
					const isEmptyArray = Array.isArray(rawValue) && !rawValue.length;
					if ((!rawValue || isEmptyArray) && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];
					const { op, value }: { op: string; value: any } = convertMuiFilterForV2Api(operator, rawValue);

					if (field === 'integration') {
						let newValue;
						if (typeof value === 'string') {
							newValue = reservationIntegrationValueByDisplay[value] || value;
						} else {
							newValue = value.map((v) => reservationIntegrationValueByDisplay[v] || v);
						}
						return {
							propertyName: field,
							op,
							value: newValue,
						};
					}

					return {
						propertyName: field,
						op,
						value,
					};
				})
				.flat();

			debouncedSetFilters(queryFilters);
		},
		[debouncedSetFilters],
	);

	const renderCustomToolbar = () => (
		<GridToolbarContainer>
			<GridToolbarFilterButton onResize={undefined} onResizeCapture={undefined} />
			<GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
			<GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
		</GridToolbarContainer>
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabNav}
			<div
				className='pca__datagrid-sticky--headfoot'
				data-cy='reservation-access-system-history-datagrid-container'
			>
				<DataGridPro
					sx={{ height: '100%' }}
					paginationModel={{ page, pageSize }}
					onSortModelChange={onSortModelChange}
					filterMode='server'
					onFilterModelChange={onFilterModelChange}
					hideFooter={false}
					hideFooterPagination={false}
					onPaginationModelChange={setPaginationModel}
					pagination
					rows={rashData || []}
					rowCount={rowCount}
					columns={columns}
					pageSizeOptions={[pageSize]}
					paginationMode='server'
					sortingMode='server'
					loading={loading}
					slots={{
						toolbar: renderCustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
					initialState={{
						filter: {
							filterModel: {
								items: [
									{
										id: 1,
										field: 'status',
										operator: 'isAnyOf',
										value: ['Failed', 'Pending', 'New'],
									},
								],
							},
						},
					}}
				/>
			</div>
		</div>
	);
};
